import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Navbar, Nav, Container, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { logout } from '../actions/userActions';
import api from '../api/axios';
import { Menu } from 'lucide-react';
import { SidebarIcon, LockIcon } from './sidebarIcons';

function EmployerSideBar() {
  const [collapsed, setCollapsed] = useState(false);
  const [, setWindowWidth] = useState(window.innerWidth);
  const previousWidthRef = useRef(window.innerWidth);
  const [showLabels, setShowLabels] = useState(!(window.innerWidth < 992));

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const userLogin = useSelector((state) => state.userLogin);
  const { userInfo } = userLogin;

  const userDetails = useSelector((state) => state.userDetails);
  const { user } = userDetails;

  const subscription = user?.subscription || userInfo?.subscription;
  const isFreeAccount = subscription?.plan?.name?.toLowerCase() === 'darmowy';

  const logoutHandler = () => {
    dispatch(logout());
    navigate('/login');
  };
  const [pendingRequests, setPendingRequests] = useState(0);

  const fetchPendingRequests = useCallback(async () => {
    try {
      const res = await api.get('/employer/vacation-requests/pending/');
      setPendingRequests((res.data || []).length);
    } catch (err) {
      console.error(err);
    }
  }, []);

  useEffect(() => {
    fetchPendingRequests();

    const handler = () => fetchPendingRequests();
    window.addEventListener('vacationRequestsUpdated', handler);
    return () => window.removeEventListener('vacationRequestsUpdated', handler);
  }, [fetchPendingRequests, location]);
  
  const toggleSidebar = () => {
    setCollapsed((prev) => !prev);
  };

  useEffect(() => {
    if (window.innerWidth < 992) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [location]);

  useEffect(() => {
    if (window.innerWidth < 992) {
      setCollapsed(true);
    } else {
      setCollapsed(false);
    }

    const handleResize = () => {
      const currentWidth = window.innerWidth;
      setWindowWidth(currentWidth); 

      if (currentWidth < 992 && previousWidthRef.current >= 992) {
        setCollapsed(true);
      } else if (currentWidth >= 992 && previousWidthRef.current < 992) {
        setCollapsed(false);
      }
      previousWidthRef.current = currentWidth;
    };

    window.addEventListener('resize', handleResize);
    
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const handleNavClick = () => {
    if (window.innerWidth < 992) {
      setTimeout(() => {
        setCollapsed(true);
      }, 50);
    }
  };

  useEffect(() => {
    let timeout;
    if (!collapsed) {
      timeout = setTimeout(() => setShowLabels(true), 300);
    } else {
      setShowLabels(false);
    }
    return () => clearTimeout(timeout);
  }, [collapsed]);

  return (
    <>
      <Navbar
        className="app-sidebar"
        style={{ 
          width: collapsed ? '70px' : '200px',
          height: '100vh',
          position: 'fixed',
          transition: 'width 0.3s ease',
          zIndex: 1000,
          overflowY: 'auto',
          overflowX: 'hidden'
        }}
      >
        <Container className="d-flex flex-column h-100 p-0">
          <div className="d-flex align-items-center ps-3 py-3 border-bottom">
            <button 
              onClick={toggleSidebar}
              className="btn btn-link p-0 me-2 app-sidebar-toggle"
              aria-label="Zwiń lub rozwiń menu"
              style={{ fontSize: '1.25rem' }}
            >
              <Menu size={20} strokeWidth={1.75} />
            </button>
            {!collapsed && (
              <Navbar.Brand 
                as={Link} 
                to="/employer-home" 
                className="mb-0 d-flex align-items-center" 
                style={{ paddingLeft: 8 }}
                onClick={handleNavClick}
              >
                <span className="pub-logo">SIGMA</span>
              </Navbar.Brand>
            )}
          </div>

          {isFreeAccount && !collapsed && (
            <div className="px-3 py-2">
              <div className="app-sidebar-plan">Konto darmowe</div>
            </div>
          )}

          <Nav className="flex-column flex-grow-1 mt-2">
            <NavItem 
              to="/employer-home"
              icon="fa-dashboard"
              text="Panel Pracodawcy"
              collapsed={collapsed}
              showLabels={showLabels}
              isLocked={false}
              onNavClick={handleNavClick}
            />

            {/* <NavItem 
              to="/employer-jobs"
              icon="fa-briefcase"
              text="Oferty pracy"
              collapsed={collapsed}
              showLabels={showLabels}
              isLocked={isFreeAccount}
              onNavClick={handleNavClick}
            />

            <NavItem 
              to="/employer-applications"
              icon="fa-file-invoice"
              text="Aplikacje"
              collapsed={collapsed}
              showLabels={showLabels}
              isLocked={isFreeAccount}
              onNavClick={handleNavClick}
            /> */}

            <NavItem 
              to="/employer-employees"
              icon="fa-users"
              text="Pracownicy"
              collapsed={collapsed}
              showLabels={showLabels}
              isLocked={isFreeAccount}
              onNavClick={handleNavClick}
            />

            <NavItem 
              to="/employer-link-requests"
              icon="fa-link"
              text="Połącz konto"
              collapsed={collapsed}
              showLabels={showLabels}
              isLocked={isFreeAccount}
              onNavClick={handleNavClick}
            />

            <NavItem 
              to="/employer-worker-vacation-requests"
              icon="fa-clipboard-check"
              text="Wnioski urlopowe"
              collapsed={collapsed}
              showLabels={showLabels}
              isLocked={isFreeAccount}
              onNavClick={handleNavClick}
              badgeCount={pendingRequests}
            />
            
            <NavItem 
              to="/employer-vacations"
              icon="fa-calendar-days"
              text="Urlopy pracowników"
              collapsed={collapsed}
              showLabels={showLabels}
              isLocked={isFreeAccount}
              onNavClick={handleNavClick}
            />

            {/* <NavItem 
              to="/employer-reports"
              icon="fa-chart-bar"
              text="Raporty"
              collapsed={collapsed}
              showLabels={showLabels}
              isLocked={isFreeAccount}
              onNavClick={handleNavClick}
            /> */}

            <NavItem 
              to="/employer-settings"
              icon="fa-cog"
              text="Ustawienia firmy"
              collapsed={collapsed}
              showLabels={showLabels}
              isLocked={isFreeAccount}
              onNavClick={handleNavClick}
            />
          </Nav>

          <div style={{ 
            width: '100%', 
            paddingBottom: window.innerWidth < 992 ? '100px' : '0px', 
            marginTop: 'auto', 
            display: 'flex', 
            flexDirection: 'column' 
          }}>
            <Nav className="flex-column">
              <NavItem 
                to="/employer-app-pricing"
                icon="fa-line-chart"
                text="Plan subskrypcji"
                collapsed={collapsed}
                showLabels={showLabels}
                isLocked={false}
                onNavClick={handleNavClick}
              />
              <NavItem 
                to="/employer-help"
                icon="fa-circle-question"
                text="Jak to działa?"
                collapsed={collapsed}
                showLabels={showLabels}
                isLocked={false}
                onNavClick={handleNavClick}
              />
              <NavItem 
                to="/employer-profile"
                icon="fa-circle-user"
                text="Mój profil"
                collapsed={collapsed}
                showLabels={showLabels}
                isLocked={false}
                onNavClick={handleNavClick}
              />

              <NavItem 
                icon="fa-right-from-bracket"
                text="Wyloguj się"
                collapsed={collapsed}
                showLabels={showLabels}
                onClick={logoutHandler}
                isLocked={false}
                onNavClick={handleNavClick}
              />
            </Nav>
          </div>
        </Container>
      </Navbar>

      <div style={{
        marginLeft: collapsed ? '70px' : '200px',
        transition: 'margin-left 0.3s ease',
        minHeight: '100vh'
      }}>
      </div>
    </>
  );
}

function NavItem({ to, icon, text, collapsed, showLabels, onClick, isLocked = false, onNavClick, badgeCount = 0 }) {
  const { pathname } = useLocation();
  const isActive = !!to && pathname === to;
  const isMobile = window.innerWidth < 992;

  const handleClick = (e) => {
    if (isLocked) {
      e.preventDefault();
      return;
    }

    if (onClick) onClick(e);
    if (onNavClick) onNavClick();

    if (collapsed) {
      setTimeout(() => {
        document.activeElement?.blur();
      }, 100);
    }
  };

  const content = (
    <span style={{ display: 'flex', alignItems: 'center', position: 'relative' }}>
      <span style={{ position: 'relative', display: 'inline-block' }}>
        <SidebarIcon name={icon} dimmed={isLocked} />
        {badgeCount > 0 && (
          <span
            style={{
              position: 'absolute',
              top: '-6px',
              right: collapsed ? '-8px' : '2px',
              background: '#e03131',
              color: 'white',
              borderRadius: '50%',
              fontSize: '10px',
              fontWeight: 700,
              minWidth: '16px',
              height: '16px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '0 3px',
              lineHeight: 1,
            }}
          >
            {badgeCount > 9 ? '9+' : badgeCount}
          </span>
        )}
      </span>
      {showLabels && (
        <span
          style={{
            marginLeft: 12,
            position: 'relative',
            display: 'inline-block',
            minWidth: 0,
            wordBreak: 'break-word',
            flex: 1,
            opacity: 0,
            transform: 'translateX(-5px)',
            animation: showLabels ? 'slideIn 0.1s ease-out forwards' : 'none'
          }}
        >
          <style>{`
            @keyframes slideIn {
              to {
                opacity: 1;
                transform: translateX(0);
              }
            }
          `}</style>
          {text}
          {isLocked && showLabels && (
            <span className="ms-2 app-sidebar-lock"><LockIcon /></span>
          )}
        </span>
      )}
    </span>
  );

  // reszta (linkProps, tooltipText, showTooltip, return) — bez zmian

  const linkProps = {
    className: `${collapsed ? "py-3" : "px-3 py-3"}${isActive ? " active" : ""}`,
    onClick: handleClick,
    style: { 
      opacity: isLocked ? 0.6 : 1,
      cursor: isLocked ? 'not-allowed' : 'pointer',
      display: 'flex',
      justifyContent: collapsed ? 'center' : 'flex-start'
    }
  };

  const tooltipText = isLocked ? "Dostępne tylko w płatnej wersji" : text;
  const showTooltip = isLocked || collapsed;

  if (isMobile) {
    return to ? (
      <Nav.Link as={isLocked ? 'div' : Link} to={isLocked ? undefined : to} {...linkProps}>
        {content}
      </Nav.Link>
    ) : (
      <Nav.Link {...linkProps}>{content}</Nav.Link>
    );
  }

  return (
    <OverlayTrigger
      placement="right"
      overlay={<Tooltip>{tooltipText}</Tooltip>}
      show={showTooltip ? undefined : false}
    >
      {to ? (
        <Nav.Link as={isLocked ? 'div' : Link} to={isLocked ? undefined : to} {...linkProps}>
          {content}
        </Nav.Link>
      ) : (
        <Nav.Link {...linkProps}>{content}</Nav.Link>
      )}
    </OverlayTrigger>
  );
}

export default EmployerSideBar;
